import { useMutation } from '@apollo/client'
import { AlertTriangle, MailQuestion } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import {
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
} from 'react'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { AuthContext, AuthDispatchContext } from '@/components/auth/context'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { REMEMBER_ME_KEY } from '@/config/local-storage'
import { gql } from '@/lib/graphql/gql'
import type {
  LoginWith2FaMutation,
  LoginWith2FaMutationVariables,
} from '@/routes/login/twofa-form.types.gen'

const LOGIN_WITH_2FA = gql`
  mutation LoginWith2FA($token: String!, $code: String!, $rememberMe: Boolean) {
    loginWith2FA(token: $token, code: $code, rememberMe: $rememberMe) {
      token
      rememberMeToken
      error
    }
  }
`

type Inputs = {
  code: string
  rememberMe: boolean
}

const SHOW_INBOX_NOTICE_AFTER_IN_MILLISECONDS = 10 * 1000 // 10 seconds
const REQUEST_NEW_CODE_COOL_DOWN_IN_MILLISECONDS = 30 * 1000 // 30 seconds
const CODE_EXPIRATION_IN_MILLISECONDS = 10 * 60 * 1000 // 10 minutes

export const TwoFAForm = ({
  loginState: { email, password, twoFAToken },
  requestNewEmailCode,
  returnTo,
}: {
  loginState: {
    email: string
    password: string
    twoFAToken: string
  }
  requestNewEmailCode: (input: { email: string; password: string }) => void
  returnTo?: string | null
}) => {
  const { twoFA } = use(AuthContext) ?? {}
  const {
    login: saveToken = () => {},
    logout = () => {},
    saveRememberMeToken,
  } = use(AuthDispatchContext) ?? {}
  const [canRequestNewCode, setCanRequestNewCode] = useState(false)
  const [isCodeExpired, setIsCodeExpired] = useState(false)
  const [isInboxNoticeVisible, setIsInboxNoticeVisible] = useState(false)
  const showInboxNoticeRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestNewCodeCountdownRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  const expirationRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isSubmitPending, startSubmitTransition] = useTransition()
  const [isNewCodePending, startNewCodeTransition] = useTransition()
  const [loginWith2FA, { data: mutationData, error }] = useMutation<
    LoginWith2FaMutation,
    LoginWith2FaMutationVariables
  >(LOGIN_WITH_2FA)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const form = useForm<Inputs>({
    defaultValues: {
      code: '',
      rememberMe: !!localStorage.getItem(REMEMBER_ME_KEY),
    },
  })
  const { handleSubmit, setValue, resetField } = form

  const twoFAType = twoFA?.type

  useEffect(() => {
    if (twoFAType === 'email') {
      showInboxNoticeRef.current = setTimeout(() => {
        setIsInboxNoticeVisible(true)
      }, SHOW_INBOX_NOTICE_AFTER_IN_MILLISECONDS)

      if (!canRequestNewCode) {
        requestNewCodeCountdownRef.current = setTimeout(
          () => setCanRequestNewCode(true),
          REQUEST_NEW_CODE_COOL_DOWN_IN_MILLISECONDS
        )
      }

      expirationRef.current = setTimeout(() => {
        setIsCodeExpired(true)
        setValue('code', '')
      }, CODE_EXPIRATION_IN_MILLISECONDS)
    }

    return () => {
      if (showInboxNoticeRef.current) {
        clearTimeout(showInboxNoticeRef.current)
        showInboxNoticeRef.current = null
      }
      if (expirationRef.current) {
        clearTimeout(expirationRef.current)
        expirationRef.current = null
      }
      if (requestNewCodeCountdownRef.current) {
        clearTimeout(requestNewCodeCountdownRef.current)
        requestNewCodeCountdownRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onRequestNewEmailCode = useCallback(() => {
    if (!canRequestNewCode || twoFAType !== 'email') {
      return
    }

    startNewCodeTransition(async () => {
      try {
        await requestNewEmailCode({
          email,
          password,
        })

        setCanRequestNewCode(false)
        setIsInboxNoticeVisible(false)

        if (isCodeExpired) {
          setIsCodeExpired(false)
        }

        setValue('code', '')

        if (showInboxNoticeRef.current) {
          clearTimeout(showInboxNoticeRef.current)
          showInboxNoticeRef.current = null
        }
        if (expirationRef.current) {
          clearTimeout(expirationRef.current)
          expirationRef.current = null
        }
        if (requestNewCodeCountdownRef.current) {
          clearTimeout(requestNewCodeCountdownRef.current)
          requestNewCodeCountdownRef.current = null
        }

        showInboxNoticeRef.current = setTimeout(() => {
          setIsInboxNoticeVisible(true)
        }, SHOW_INBOX_NOTICE_AFTER_IN_MILLISECONDS)

        expirationRef.current = setTimeout(() => {
          setIsCodeExpired(true)
          setValue('code', '')
        }, CODE_EXPIRATION_IN_MILLISECONDS)

        requestNewCodeCountdownRef.current = setTimeout(
          () => setCanRequestNewCode(true),
          REQUEST_NEW_CODE_COOL_DOWN_IN_MILLISECONDS
        )
      } catch (error) {
        console.error('Error requesting new email code:', error)
      }
    })
  }, [
    canRequestNewCode,
    twoFAType,
    requestNewEmailCode,
    email,
    password,
    isCodeExpired,
    setValue,
  ])

  const onSubmit: SubmitHandler<Inputs> = useCallback(
    ({ code, rememberMe }) =>
      startSubmitTransition(async () => {
        if (!twoFAToken) {
          console.error('No twoFA token provided')
          return
        }

        try {
          const { data } = await loginWith2FA({
            variables: {
              token: twoFAToken,
              code,
              rememberMe,
            },
          })

          if (data?.loginWith2FA?.rememberMeToken) {
            saveRememberMeToken?.(data?.loginWith2FA?.rememberMeToken)
          }

          if (data?.loginWith2FA?.token) {
            saveToken(data.loginWith2FA.token)
            navigate(returnTo ?? '/')
          } else if (data?.loginWith2FA?.error) {
            resetField('code')
            inputRef.current?.focus()
          }
        } catch (error) {
          console.error('Error during login with 2FA:', error)
          // Handle error (e.g., show a notification)
        }
      }),
    [
      loginWith2FA,
      twoFAToken,
      saveToken,
      navigate,
      resetField,
      saveRememberMeToken,
      returnTo,
    ]
  )

  return (
    <>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-6">
            <AnimatePresence>
              {mutationData?.loginWith2FA?.error || error ? (
                <motion.div
                  key="alert"
                  layout
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <Alert variant="destructive" className="mx-auto w-fit">
                    <AlertTriangle />
                    <AlertTitle>
                      {mutationData?.loginWith2FA?.error ??
                        error?.message ??
                        'Unexpected error'}
                    </AlertTitle>
                  </Alert>
                </motion.div>
              ) : null}
            </AnimatePresence>
            <FormField
              control={form.control}
              name="code"
              rules={{
                required: 'Authentication code is required',
              }}
              render={({ field: { value, onChange, ...field } }) => (
                <FormItem className="mx-auto">
                  <FormControl>
                    <InputOTP
                      aria-label="6-digit authentication code"
                      autoFocus
                      autoComplete="one-time-code"
                      maxLength={6}
                      disabled={isCodeExpired || isSubmitPending}
                      value={value}
                      onChange={onChange}
                      {...field}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot ref={inputRef} index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="rememberMe"
              render={({ field: { value, onChange, ...field } }) => (
                <FormItem className="mx-auto flex items-center gap-2">
                  <FormControl>
                    <Checkbox
                      id="rememberMe"
                      disabled={isCodeExpired || isSubmitPending}
                      checked={value}
                      onCheckedChange={onChange}
                      {...field}
                    />
                  </FormControl>
                  <FormLabel htmlFor="rememberMe">
                    Don't ask for a code on this device
                  </FormLabel>
                </FormItem>
              )}
            />
            <AnimatePresence>
              {twoFAType === 'email' &&
              (isInboxNoticeVisible || canRequestNewCode) &&
              !isCodeExpired ? (
                <motion.div
                  key="request-new-code"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <Alert>
                    <MailQuestion />
                    <AlertTitle>Didn't receive an email?</AlertTitle>
                    <AlertDescription>
                      Double-check your inbox/spam folders.{' '}
                      {!canRequestNewCode ? (
                        'In 30 seconds you can request a new code.'
                      ) : (
                        <>
                          <br />
                          <Button
                            type="button"
                            className="px-0"
                            variant="link"
                            disabled={isNewCodePending}
                            onClick={onRequestNewEmailCode}
                          >
                            Request a new code
                          </Button>
                        </>
                      )}
                    </AlertDescription>
                  </Alert>
                </motion.div>
              ) : null}
              {twoFAType === 'email' && isCodeExpired ? (
                <motion.div
                  key="code-expired"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <Alert variant="destructive">
                    <AlertTriangle />
                    <AlertTitle>Code has expired</AlertTitle>
                    <AlertDescription>
                      Please request a new one!
                    </AlertDescription>
                  </Alert>
                </motion.div>
              ) : null}
            </AnimatePresence>
            <div className="grid grid-cols-9 gap-3">
              <Button
                type="button"
                variant="secondary"
                className="col-start-1 col-span-2"
                onClick={logout}
              >
                Back
              </Button>
              {twoFAType === 'email' && isCodeExpired ? (
                <Button
                  className="col-start-3 col-span-7"
                  type="button"
                  disabled={isNewCodePending}
                  onClick={onRequestNewEmailCode}
                >
                  Request a new code
                </Button>
              ) : (
                <Button
                  className="col-start-3 col-span-7"
                  type="submit"
                  disabled={isCodeExpired || isSubmitPending}
                >
                  Verify
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
